import { authenticationInstance, authServerInstance } from "../axiosInstance";
import { addAndUpload } from "./documentUpload";
import {
  authServerUrl,
  clientId,
  clientSecret,
  gatewayUrl,
  localStorageKeys,
  redirectUri,
  SERVICE_ENDPOINT,
} from "../const";
import axios from "axios";

// Small helper to keep things tidy
const getPublicIPSafe = async () => {
  try {
    const ipResponse = await fetch("https://api.ipify.org?format=json");
    if (!ipResponse.ok) return "";
    const data = await ipResponse.json().catch(() => null);
    return data && typeof data.ip === "string" ? data.ip : "";
  } catch {
    // network error, CORS, etc.
    return "";
  }
};

export const exchangeAuthCode = async (code) => {
  const ip = await getPublicIPSafe(); // empty string if anything goes wrong

  const data = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: redirectUri,
    client_id: clientId,
    client_secret: clientSecret,
  });

  const response = await authenticationInstance.post("/token", data, {
    headers: {
      Authorization: `Basic ${btoa(clientId + ":" + clientSecret)}`,
      "Content-Type": "application/x-www-form-urlencoded",
      "X-Client-IP": ip, // will be "" if unavailable
    },
  });
  return response.data;
};

export const exchangeRefreshCode = async () => {
  if (!localStorage.getItem(localStorageKeys.REFRESH_TOKEN)) {
    throw new Error("No Refresh Token");
  }
  const data = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: localStorage.getItem(localStorageKeys.REFRESH_TOKEN),
  });

  const response = await authenticationInstance.post("/token", data);
  return response.data;
};

export const fetchAllowedRedirects = async () => {
  try {
    const response = await axios.get(`${authServerUrl}/allowed-redirects`);
    return response.data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const fetchPlayStoreUrl = async () => {
  try {
    const response = await axios.get(
      `${authServerUrl}/allowed-redirects/play-store`,
    );
    return response.data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const fetchAppStoreUrl = async () => {
  try {
    const response = await axios.get(
      `${authServerUrl}/allowed-redirects/app-store`,
    );
    return response.data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const fetchUserAuthInfo = async () => {
  try {
    const response = await authServerInstance.get("/user-auth-info");
    return response.data;
  } catch (e) {
    if (e.message !== "No Refresh Token") {
      console.error(e);
    }
    // Never resolve with an empty payload - the caller would treat it as a
    // successful load and render the app with no user, company or permission.
    throw e;
  }
};

export const updateUserCompany = async (data) => {
  try {
    const response = await authServerInstance.put(
      "/user-auth-info/user-company",
      data,
    );
    return response.data;
  } catch (e) {
    console.error(e);
    return -1;
  }
};

export const fetchInitUserAuthInfo = async (authHeader) => {
  try {
    const response = await axios.get(`${authServerUrl}/user-auth-info`, {
      headers: {
        Authorization: authHeader,
      },
    });
    return response.data;
  } catch (e) {
    console.error(e);
    // A partial auth payload must never look like a successful load - the
    // caller would store an undefined company/permission set.
    throw e;
  }
};

export const fetchMobileLongUrlByUuid = async (uuid) => {
  try {
    const response = await axios.post(
      `${authServerUrl}/deviceReg-url-fetch/fetch-long-url/${uuid}`,
    );
    return response.data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const uploadProfilePicture = async (file, userSeq, username) => {
  // 1. Follow exact Vehicle Creation upload pattern: POST to /transportservice/document/upload
  const formData = new FormData();
  formData.append("multipartFile", file);
  formData.append(
    "documentRequest",
    JSON.stringify({
      docOperation: 32,
      docReference: userSeq ? Number(userSeq) : 1,
      docCategory: 1,
      fileName: 79,
      docName: file.name,
      docStatus: 1,
      docActive: 1,
      author: username || "user",
    }),
  );
  formData.append("isImage", true);

  const uploadRes = await addAndUpload(formData);

  if (uploadRes && uploadRes.docId) {
    // 2. Associate generated docId with user record in authservice
    const linkRes = await authServerInstance.put(
      "/console/user/profile-picture-doc",
      {
        docId: uploadRes.docId,
        userSeq: userSeq ? Number(userSeq) : undefined,
      },
    );
    return {
      ...(typeof linkRes.data === "object" ? linkRes.data : { message: linkRes.data }),
      docId: uploadRes.docId,
    };
  }

  // Fallback direct upload via authservice if needed
  const fallbackData = new FormData();
  fallbackData.append("file", file);
  const response = await authServerInstance.put(
    "/console/user/profile-picture",
    fallbackData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: userSeq ? { userSeq } : {},
    },
  );
  return response.data;
};

export const removeProfilePicture = async (userSeq) => {
  const response = await authServerInstance.delete(
    "/console/user/profile-picture",
    {
      params: userSeq ? { userSeq } : {},
    },
  );
  return response.data;
};

export const getProfilePictureUrl = (docId) => {
  if (!docId) return null;
  return `${gatewayUrl}${SERVICE_ENDPOINT.TRANSPORT}/document/download/downscaled/${docId}`;
};
